import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api, COPY_FIELDS } from '@/lib/api';
import type { CopyField, CopyTexts, CopyTranslationPayload } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import ListaAnuncios from '@/components/copy/ListaAnuncios';
import GenerarTodoFlujo from '@/components/copy/GenerarTodoFlujo';
import TraducirTodoFlujo from '@/components/copy/TraducirTodoFlujo';
import ModalTitulos from '@/components/copy/ModalTitulos';
import ModalHistorial from '@/components/copy/ModalHistorial';
import { AYUDA_CAMPO, ETIQUETAS_CAMPO, IDIOMAS } from '@/components/copy/tipos';
import type { MapaIdiomas, OpcionesIA } from '@/components/copy/tipos';
import {
  AlertTriangle, Globe2, History, Languages, Lightbulb, List, Loader2, Plus,
  Save, Sparkles, Star, Trash2, X,
} from 'lucide-react';

/**
 * Idioma en el que la IA genera y desde el que traduce.
 *
 * Está fijado a ES, igual que en la app original: los prompts del backend están
 * escritos para producir castellano y la traducción masiva parte siempre de ahí.
 * Si esto pasara a ser el idioma activo, un anuncio con el editor abierto en RU
 * generaría textos rusos con prompts pensados para el español.
 */
const IDIOMA_IA = 'es';

/** Idioma con el que Hostify crea los anuncios. No se puede borrar. */
const IDIOMA_CREACION = 'en';

/** Los cuatro idiomas que Sereno crea en todos los anuncios. */
const IDIOMAS_PRINCIPALES = ['de', 'es', 'it', 'fr'];

const MAX_TITULO = 50;

type TonoAviso = 'ok' | 'error' | 'info';

interface EstadoCampo {
  ocupado?: 'guardar' | 'ia' | 'traducir';
  mensaje?: string;
  tono?: TonoAviso;
}

type EstadosCampo = Partial<Record<CopyField, EstadoCampo>>;

const CLASES_TONO: Record<TonoAviso, string> = {
  ok: 'text-green-600',
  error: 'text-rose-600',
  info: 'text-blue-600',
};

/** Se queda solo con los 14 campos conocidos: Hostify devuelve mucho más. */
function extraerTextos(origen: unknown): CopyTexts {
  const textos: CopyTexts = {};
  if (!origen || typeof origen !== 'object') return textos;
  const fuente = origen as Record<string, unknown>;
  for (const campo of COPY_FIELDS) {
    const valor = fuente[campo];
    if (typeof valor === 'string') textos[campo] = valor;
  }
  return textos;
}

/** Textarea que crece con el contenido, como los del Copywriter Manager. */
function TextareaCrece({ id, valor, onChange }: {
  id: string;
  valor: string;
  onChange: (valor: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Se mide tras pintar y antes de que el navegador dibuje, para que el salto de
  // altura no se vea. Hay que devolverla a auto antes: si no, scrollHeight nunca
  // baja al borrar texto.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [valor]);

  return (
    <textarea
      id={id}
      ref={ref}
      rows={3}
      className="input resize-none overflow-hidden min-h-[5rem]"
      value={valor}
      onChange={e => onChange(e.target.value)}
    />
  );
}

export default function CopyPage() {
  const { data: listado, loading: cargandoAnuncios } = useApi(() => api.getCopyListings(), []);
  // El indicador de "optimizada" es informativo: si el endpoint falla, la
  // pantalla sigue siendo usable con todo marcado como pendiente.
  const { data: datosOptimizados } = useApi(
    () => api.getCopyOptimized().catch(() => ({ success: false, optimized_ids: [] as number[] })),
    [],
  );

  const [optimizados, setOptimizados] = useState<Set<number>>(new Set());
  const [anuncioId, setAnuncioId] = useState<number | null>(null);
  const [fichaAnuncio, setFichaAnuncio] = useState<any>(null);
  const [idiomaBase, setIdiomaBase] = useState('en');
  const [mapaIdiomas, setMapaIdiomas] = useState<MapaIdiomas>({});
  const [idiomaActivo, setIdiomaActivo] = useState<string | null>(null);

  const [cargandoFicha, setCargandoFicha] = useState(false);
  const [errorFicha, setErrorFicha] = useState<string | null>(null);
  const [avisoTraducciones, setAvisoTraducciones] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [avisoAccion, setAvisoAccion] = useState<string | null>(null);

  const [estadoCampos, setEstadoCampos] = useState<EstadosCampo>({});
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const [panelAbierto, setPanelAbierto] = useState(false);
  const [mostrarAltaIdioma, setMostrarAltaIdioma] = useState(false);
  const [nuevoIdioma, setNuevoIdioma] = useState('');
  const [creandoIdioma, setCreandoIdioma] = useState(false);
  const [progresoPrincipales, setProgresoPrincipales] = useState<string | null>(null);

  const [campoIA, setCampoIA] = useState<CopyField | null>(null);
  const [opcionesIA, setOpcionesIA] = useState<OpcionesIA>({
    incluirAmenities: true, incluirFotos: true, instrucciones: '',
  });
  const [titulosAbierto, setTitulosAbierto] = useState(false);
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [generarTodoAbierto, setGenerarTodoAbierto] = useState(false);
  const [traducirTodoAbierto, setTraducirTodoAbierto] = useState(false);

  // Guardia de obsolescencia: dos clics seguidos en la lista no pueden dejar en
  // pantalla la ficha del anuncio anterior porque su respuesta llegue la última.
  const generacion = useRef(0);
  const temporizadores = useRef<Partial<Record<CopyField, number>>>({});

  useEffect(() => {
    if (datosOptimizados) setOptimizados(new Set(datosOptimizados.optimized_ids || []));
  }, [datosOptimizados]);

  useEffect(() => {
    const pendientes = temporizadores.current;
    return () => { Object.values(pendientes).forEach(t => window.clearTimeout(t)); };
  }, []);

  const anuncios = listado?.listings ?? [];
  const listing = fichaAnuncio?.listing ?? fichaAnuncio ?? null;
  const numFotos = Array.isArray(fichaAnuncio?.photos) ? fichaAnuncio.photos.length : 0;
  const textos: CopyTexts = (idiomaActivo && mapaIdiomas[idiomaActivo]) || {};

  const idiomas = useMemo(
    () => Object.keys(mapaIdiomas).sort((a, b) => {
      if (a === idiomaBase) return -1;
      if (b === idiomaBase) return 1;
      return a.localeCompare(b);
    }),
    [mapaIdiomas, idiomaBase],
  );

  const faltanPrincipales = IDIOMAS_PRINCIPALES.filter(l => !mapaIdiomas[l]);

  /**
   * Textos de partida para la generación completa: se prefiere lo que ya hay en
   * ES y, si está vacío, lo del idioma base. Así la IA reescribe sobre algo en
   * vez de inventar desde cero.
   */
  const textosReferencia = useMemo(() => {
    const es = mapaIdiomas[IDIOMA_IA] || {};
    const base = mapaIdiomas['en'] || mapaIdiomas[idiomaBase] || {};
    const referencia: CopyTexts = {};
    for (const campo of COPY_FIELDS) {
      referencia[campo] = (es[campo] || '').trim() || (base[campo] || '').trim();
    }
    return referencia;
  }, [mapaIdiomas, idiomaBase]);

  const camposTraduccion = useMemo(() => {
    const origen = mapaIdiomas[IDIOMA_IA] || {};
    const conTexto: CopyTexts = {};
    for (const campo of COPY_FIELDS) {
      const valor = (origen[campo] || '').trim();
      if (valor) conTexto[campo] = valor;
    }
    return conTexto;
  }, [mapaIdiomas]);

  const destinosTraduccion = useMemo(
    () => idiomas.filter(l => l !== IDIOMA_IA),
    [idiomas],
  );

  // ── Estado por campo ────────────────────────────────────────────────────

  const marcarCampo = (campo: CopyField, cambio: EstadoCampo) => {
    setEstadoCampos(prev => {
      const siguiente: EstadosCampo = { ...prev };
      siguiente[campo] = { ...prev[campo], ...cambio };
      return siguiente;
    });
  };

  const avisarCampo = (campo: CopyField, mensaje: string, tono: TonoAviso, ms = 4000) => {
    marcarCampo(campo, { mensaje, tono });
    window.clearTimeout(temporizadores.current[campo]);
    if (ms > 0) {
      temporizadores.current[campo] = window.setTimeout(
        () => marcarCampo(campo, { mensaje: undefined, tono: undefined }),
        ms,
      );
    }
  };

  const editar = (campo: CopyField, valor: string) => {
    if (!idiomaActivo) return;
    setMapaIdiomas(prev => {
      const textosIdioma: CopyTexts = { ...(prev[idiomaActivo] || {}) };
      textosIdioma[campo] = valor;
      const siguiente: MapaIdiomas = { ...prev };
      siguiente[idiomaActivo] = textosIdioma;
      return siguiente;
    });
  };

  // ── Carga ───────────────────────────────────────────────────────────────

  const cargar = async (id: number) => {
    const mia = ++generacion.current;
    setCargandoFicha(true);
    setErrorFicha(null);
    setAvisoTraducciones(null);

    try {
      // La ficha y las traducciones se piden por separado a propósito: son dos
      // endpoints de Hostify y el segundo falla más. Si cae, el idioma base ya
      // está en mano y la pantalla se puede usar igual.
      const detalle = await api.getCopyListing(id);
      const ficha = detalle?.listing ?? detalle ?? {};
      const descripcion = detalle?.description ?? {};
      // El idioma de creación en Hostify es siempre el inglés; el `lang` del
      // registro base solo dice en qué idioma está el texto que hay ahí dentro,
      // y lleva tiempo diciendo `es` porque esta herramienta escribía español
      // en él. Tomarlo por "idioma base" es lo que rompía el guardado.
      const base = IDIOMA_CREACION;

      const mapa: MapaIdiomas = {};
      let aviso: string | null = null;
      try {
        const respuesta = await api.getCopyTranslations(id);
        const lista = Array.isArray(respuesta)
          ? respuesta
          : (respuesta?.translation ?? respuesta?.translations ?? respuesta?.data ?? []);
        if (Array.isArray(lista)) {
          for (const traduccion of lista) {
            const lang = String(traduccion?.language ?? traduccion?.lang ?? '').toLowerCase();
            if (lang) mapa[lang] = extraerTextos(traduccion);
          }
        }
      } catch {
        aviso = 'No se pudieron cargar las traducciones de Hostify.';
      }

      // Red de seguridad para un anuncio que aún no tenga ninguna traducción:
      // se enseña el registro base bajo el idioma de creación para no dejar la
      // pantalla en blanco. En cuanto se guarde, irá a su traducción.
      if (Object.keys(mapa).length === 0) {
        const textosBase = extraerTextos(descripcion);
        if (typeof textosBase.name !== 'string') textosBase.name = String(ficha.name || '');
        mapa[base] = textosBase;
      }

      if (mia !== generacion.current) return;
      setFichaAnuncio(detalle);
      setIdiomaBase(base);
      setMapaIdiomas(mapa);
      // Se abre por el idioma de trabajo, que es desde el que se traduce.
      setIdiomaActivo(mapa[IDIOMA_IA] ? IDIOMA_IA : (mapa[base] ? base : Object.keys(mapa)[0] ?? null));
      setAvisoTraducciones(aviso);
    } catch (err: any) {
      if (mia !== generacion.current) return;
      setErrorFicha(err?.message || 'No se pudo cargar el anuncio');
    } finally {
      if (mia === generacion.current) setCargandoFicha(false);
    }
  };

  const seleccionar = (id: number) => {
    setPanelAbierto(false);
    if (id === anuncioId) return;
    setAnuncioId(id);
    setFichaAnuncio(null);
    setMapaIdiomas({});
    setIdiomaActivo(null);
    setEstadoCampos({});
    setErrorAccion(null);
    setAvisoAccion(null);
    setMostrarAltaIdioma(false);
    void cargar(id);
  };

  // ── Guardado ────────────────────────────────────────────────────────────

  /**
   * Guarda un idioma. Todos van por la API de traducciones, sin excepciones.
   *
   * Hostify enseña sus pestañas de idioma leyendo SOLO las traducciones; el
   * registro base (`description`) no se ve en ninguna de sus pantallas. Antes
   * el "idioma base" se guardaba en ese registro invisible, así que editar en
   * español no cambiaba nada en la pestaña española de Hostify.
   */
  const persistir = async (lang: string, campos: CopyTexts) => {
    if (!anuncioId) return;
    const payload: CopyTranslationPayload[] = [{ ...campos, lang }];
    try {
      await api.updateCopyTranslations(anuncioId, payload);
    } catch {
      // Hostify rechaza el PUT cuando la traducción todavía no existe; crearla
      // es el camino correcto y no hay endpoint para distinguirlo antes.
      await api.createCopyTranslations(anuncioId, payload);
    }
  };

  const guardarCampo = async (campo: CopyField) => {
    if (!anuncioId || !idiomaActivo) return;
    marcarCampo(campo, { ocupado: 'guardar', mensaje: undefined, tono: undefined });
    try {
      const uno: CopyTexts = {};
      uno[campo] = textos[campo] ?? '';
      await persistir(idiomaActivo, uno);
      avisarCampo(campo, 'Guardado ✓', 'ok');
    } catch (err: any) {
      avisarCampo(campo, err?.message || 'Error al guardar', 'error', 0);
    } finally {
      marcarCampo(campo, { ocupado: undefined });
    }
  };

  const guardarTodo = async () => {
    if (!anuncioId || !idiomaActivo) return;
    setGuardando(true);
    setErrorAccion(null);
    setAvisoAccion(null);
    try {
      // Se mandan los 14, también los vacíos: es la forma de borrar en Hostify
      // un campo que el usuario acaba de dejar en blanco.
      const completos: CopyTexts = {};
      for (const campo of COPY_FIELDS) completos[campo] = textos[campo] ?? '';
      await persistir(idiomaActivo, completos);
      setAvisoAccion(`Todos los campos guardados en ${idiomaActivo.toUpperCase()}`);
    } catch (err: any) {
      setErrorAccion(err?.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  // ── IA por campo ────────────────────────────────────────────────────────

  const abrirOpcionesIA = (campo: CopyField) => {
    setOpcionesIA(prev => ({
      incluirAmenities: prev.incluirAmenities,
      incluirFotos: numFotos > 0 && prev.incluirFotos,
      instrucciones: '',
    }));
    setCampoIA(campo);
  };

  const generarCampo = async (campo: CopyField, opciones: OpcionesIA) => {
    if (!anuncioId || !idiomaActivo) return;
    setCampoIA(null);
    marcarCampo(campo, { ocupado: 'ia' });
    avisarCampo(campo, 'Generando con IA…', 'info', 0);
    try {
      const res = await api.generateCopyField(anuncioId, {
        field: campo,
        language: idiomaActivo,
        current_value: textos[campo] ?? '',
        include_amenities: opciones.incluirAmenities,
        include_photos: opciones.incluirFotos && numFotos > 0,
        extra_context: opciones.instrucciones.trim() || undefined,
      });
      if (!res.success || !res.text) throw new Error('La IA no devolvió texto');
      editar(campo, res.text);
      avisarCampo(campo, 'Texto de IA insertado — pulsa Guardar para aplicarlo', 'info', 8000);
    } catch (err: any) {
      avisarCampo(campo, err?.message || 'Error generando con IA', 'error', 0);
    } finally {
      marcarCampo(campo, { ocupado: undefined });
    }
  };

  const traducirCampo = async (campo: CopyField) => {
    if (!anuncioId || !idiomaActivo) return;
    const texto = (textos[campo] ?? '').trim();
    if (!texto) {
      avisarCampo(campo, 'El campo está vacío — escribe algo antes de traducir', 'error');
      return;
    }
    const destinos = idiomas.filter(l => l !== idiomaActivo);
    if (destinos.length === 0) {
      avisarCampo(campo, 'No hay otros idiomas a los que traducir', 'error');
      return;
    }
    const etiquetas = destinos.map(l => l.toUpperCase()).join(', ');
    if (!confirm(`¿Traducir "${ETIQUETAS_CAMPO[campo]}" a ${etiquetas}? Se guardará automáticamente.`)) return;

    marcarCampo(campo, { ocupado: 'traducir' });
    avisarCampo(campo, 'Traduciendo y guardando…', 'info', 0);
    try {
      const res = await api.translateCopyField(anuncioId, {
        field: campo,
        source_lang: idiomaActivo,
        source_text: texto,
        target_langs: destinos,
      });

      // El servidor traduce y guarda; aquí solo ponemos al día la caché para no
      // tener que recargar la ficha entera y perder lo editado sin guardar.
      setMapaIdiomas(prev => {
        const siguiente: MapaIdiomas = { ...prev };
        for (const [lang, valor] of Object.entries(res.translations || {})) {
          if (!siguiente[lang]) continue;
          const textosIdioma: CopyTexts = { ...siguiente[lang] };
          textosIdioma[campo] = valor;
          siguiente[lang] = textosIdioma;
        }
        return siguiente;
      });

      const fallidos = res.errors || [];
      const guardados = res.saved?.length ?? destinos.length;
      if (fallidos.length > 0) {
        avisarCampo(
          campo,
          `Guardado en ${guardados} idioma(s); falló ${fallidos.map(e => e.lang.toUpperCase()).join(', ')}`,
          'error', 0,
        );
      } else {
        avisarCampo(campo, `Traducido y guardado en ${guardados} idioma(s)`, 'ok', 6000);
      }
    } catch (err: any) {
      avisarCampo(campo, err?.message || 'Error al traducir', 'error', 0);
    } finally {
      marcarCampo(campo, { ocupado: undefined });
    }
  };

  // ── Idiomas ─────────────────────────────────────────────────────────────

  const crearIdioma = async (lang: string) => {
    if (!anuncioId) return;
    const vacio: CopyTexts = {};
    for (const campo of COPY_FIELDS) vacio[campo] = '';
    await api.createCopyTranslations(anuncioId, [{ ...vacio, lang }]);
    setMapaIdiomas(prev => {
      const siguiente: MapaIdiomas = { ...prev };
      siguiente[lang] = vacio;
      return siguiente;
    });
  };

  const anadirIdioma = async () => {
    if (!nuevoIdioma || mapaIdiomas[nuevoIdioma]) return;
    setCreandoIdioma(true);
    setErrorAccion(null);
    try {
      await crearIdioma(nuevoIdioma);
      setIdiomaActivo(nuevoIdioma);
      setMostrarAltaIdioma(false);
      setNuevoIdioma('');
    } catch (err: any) {
      setErrorAccion(err?.message || 'No se pudo crear el idioma');
    } finally {
      setCreandoIdioma(false);
    }
  };

  const crearPrincipales = async () => {
    const faltan = IDIOMAS_PRINCIPALES.filter(l => !mapaIdiomas[l]);
    if (faltan.length === 0) return;
    setErrorAccion(null);
    setAvisoAccion(null);
    const fallidos: string[] = [];
    // Uno detrás de otro, no en paralelo: Hostify se atraganta con varias altas
    // de traducción simultáneas sobre el mismo anuncio.
    for (let i = 0; i < faltan.length; i++) {
      setProgresoPrincipales(`Creando ${faltan[i].toUpperCase()}… (${i + 1}/${faltan.length})`);
      try {
        await crearIdioma(faltan[i]);
      } catch {
        fallidos.push(faltan[i].toUpperCase());
      }
    }
    setProgresoPrincipales(null);
    if (fallidos.length > 0) setErrorAccion(`No se pudieron crear: ${fallidos.join(', ')}`);
    else setAvisoAccion(`Creados ${faltan.map(l => l.toUpperCase()).join(', ')}`);
  };

  const eliminarIdioma = async () => {
    if (!anuncioId || !idiomaActivo || idiomaActivo === idiomaBase) return;
    const lang = idiomaActivo;
    if (!confirm(`¿Eliminar todas las traducciones en ${lang.toUpperCase()}?`)) return;
    setEliminando(true);
    setErrorAccion(null);
    try {
      await api.deleteCopyTranslations(anuncioId, [lang]);
      setMapaIdiomas(prev => {
        const siguiente: MapaIdiomas = { ...prev };
        delete siguiente[lang];
        return siguiente;
      });
      setIdiomaActivo(idiomaBase);
      setAvisoAccion(`Idioma ${lang.toUpperCase()} eliminado`);
    } catch (err: any) {
      setErrorAccion(err?.message || 'No se pudo eliminar el idioma');
    } finally {
      setEliminando(false);
    }
  };

  // ── Flujos globales ─────────────────────────────────────────────────────

  const abrirGenerarTodo = async () => {
    if (!anuncioId) return;
    setErrorAccion(null);
    if (!mapaIdiomas[IDIOMA_IA]) {
      if (!confirm('El idioma ES no existe todavía. ¿Lo creamos para generar los textos con IA?')) return;
      try {
        await crearIdioma(IDIOMA_IA);
      } catch (err: any) {
        setErrorAccion(err?.message || 'No se pudo crear el idioma ES');
        return;
      }
    }
    setIdiomaActivo(IDIOMA_IA);
    setGenerarTodoAbierto(true);
  };

  const aplicarGeneracion = async (nuevos: CopyTexts) => {
    if (!anuncioId) return;
    await persistir(IDIOMA_IA, nuevos);
    setMapaIdiomas(prev => {
      const siguiente: MapaIdiomas = { ...prev };
      siguiente[IDIOMA_IA] = { ...(prev[IDIOMA_IA] || {}), ...nuevos };
      return siguiente;
    });
    setOptimizados(prev => new Set(prev).add(anuncioId));
    setIdiomaActivo(IDIOMA_IA);
    setGenerarTodoAbierto(false);
    setAvisoAccion('Textos generados y guardados en ES');
  };

  const abrirTraducirTodo = () => {
    if (!anuncioId) return;
    if (!mapaIdiomas[IDIOMA_IA]) {
      alert('No hay textos en ES para traducir. Genéralos primero.');
      return;
    }
    if (destinosTraduccion.length === 0) {
      alert('No hay otros idiomas a los que traducir. Añade alguno primero.');
      return;
    }
    if (Object.keys(camposTraduccion).length === 0) {
      alert('Los campos en ES están vacíos.');
      return;
    }
    setErrorAccion(null);
    setTraducirTodoAbierto(true);
  };

  const aplicarHistorial = (campos: Record<string, string>) => {
    if (!idiomaActivo) return;
    const validos = extraerTextos(campos);
    setMapaIdiomas(prev => {
      const siguiente: MapaIdiomas = { ...prev };
      siguiente[idiomaActivo] = { ...(prev[idiomaActivo] || {}), ...validos };
      return siguiente;
    });
    setHistorialAbierto(false);
    setAvisoAccion(`${Object.keys(validos).length} campo(s) recuperados — pulsa Guardar para confirmarlos`);
  };

  const elegirTitulo = (titulo: string) => {
    editar('name', titulo);
    setTitulosAbierto(false);
    avisarCampo('name', 'Título aplicado — pulsa Guardar para confirmarlo', 'info', 8000);
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div>
      <button
        type="button"
        onClick={() => setPanelAbierto(true)}
        className="lg:hidden btn-secondary text-sm flex items-center gap-2 mb-4"
      >
        <List size={16} /> Anuncios
      </button>

      <div className="lg:flex lg:gap-6 lg:items-start">
        {panelAbierto && (
          <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setPanelAbierto(false)} />
        )}

        {/* Cajón deslizante en móvil, columna pegajosa en escritorio */}
        <aside
          className={classNames(
            'fixed top-0 left-0 z-40 h-full w-80 max-w-[85vw] p-4 bg-sereno-white flex flex-col transition-transform duration-200',
            'lg:sticky lg:top-6 lg:z-auto lg:h-[calc(100vh-3rem)] lg:w-72 lg:max-w-none lg:shrink-0 lg:p-0 lg:bg-transparent lg:translate-x-0',
            panelAbierto ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between mb-3 lg:hidden">
            <span className="font-semibold">Anuncios</span>
            <button type="button" onClick={() => setPanelAbierto(false)} className="p-1" aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
          <ListaAnuncios
            anuncios={anuncios}
            optimizados={optimizados}
            seleccionado={anuncioId}
            cargando={cargandoAnuncios}
            onSeleccionar={seleccionar}
          />
        </aside>

        <div className="flex-1 min-w-0">
          {!anuncioId ? (
            <EmptyState
              title="Selecciona un anuncio"
              description="Elige un anuncio de la lista para editar, generar o traducir sus textos."
            />
          ) : cargandoFicha ? (
            <LoadingSpinner />
          ) : errorFicha ? (
            <div className="card">
              <p className="text-rose-600 text-sm">{errorFicha}</p>
              <button onClick={() => void cargar(anuncioId)} className="btn-secondary text-sm mt-3">
                Reintentar
              </button>
            </div>
          ) : (
            <>
              {/* Cabecera del anuncio */}
              <div className="card mb-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold truncate">
                      {listing?.nickname || listing?.name || `Anuncio #${anuncioId}`}
                    </h2>
                    <p className="text-sm text-slate-500">
                      ID: {anuncioId}
                      {listing?.city ? ` · ${listing.city}` : ''}
                      {listing?.country ? ` (${listing.country})` : ''}
                    </p>
                  </div>
                  {/* En móvil, rejilla: apiladas ocupaban tres filas enteras y
                      empujaban los textos —lo que se viene a hacer— fuera de la
                      pantalla. Historial ocupa el ancho porque es la acción
                      secundaria de las tres. */}
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                    <button onClick={() => void abrirGenerarTodo()} className="btn-secondary text-sm flex items-center justify-center gap-1.5 py-2.5 sm:py-2">
                      <Sparkles size={15} className="shrink-0" /> Generar todo con IA
                    </button>
                    <button onClick={abrirTraducirTodo} className="btn-secondary text-sm flex items-center justify-center gap-1.5 py-2.5 sm:py-2">
                      <Languages size={15} className="shrink-0" /> Traducir todo con IA
                    </button>
                    <button onClick={() => setHistorialAbierto(true)} className="btn-ghost text-sm flex items-center justify-center gap-1.5 col-span-2 py-2.5 sm:py-2 sm:col-span-1">
                      <History size={15} className="shrink-0" /> Historial IA
                    </button>
                  </div>
                </div>
                {/* El nickname es el nombre interno del piso y se gestiona en
                    Hostify: esta pantalla nunca lo toca. */}
                <p className="text-xs text-slate-500 mt-3">
                  El nickname no se edita desde aquí. Lo que se edita abajo es el título del anuncio.
                </p>
              </div>

              {avisoTraducciones && (
                <div className="card mb-4 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border-amber-200">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <span>{avisoTraducciones}</span>
                </div>
              )}
              {errorAccion && <p className="text-sm text-rose-600 mb-4">{errorAccion}</p>}
              {avisoAccion && <p className="text-sm text-green-600 mb-4">{avisoAccion}</p>}

              {/* Pestañas de idioma */}
              <div className="border-b border-slate-200 dark:border-white/10 mb-4">
                <nav className="flex gap-1 -mb-px overflow-x-auto scrollbar-none">
                  {idiomas.map(lang => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setIdiomaActivo(lang)}
                      title={lang === idiomaBase ? 'Idioma base' : undefined}
                      className={classNames(
                        'flex items-center gap-1.5 px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors',
                        lang === idiomaActivo
                          ? 'border-sereno-dark text-sereno-dark font-medium dark:border-white dark:text-white'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
                      )}
                    >
                      {lang.toUpperCase()}
                      {lang === idiomaBase && <Star size={11} className="fill-current text-amber-500" />}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Acciones de idioma */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {mostrarAltaIdioma ? (
                  <>
                    <select
                      className="input w-auto text-sm"
                      value={nuevoIdioma}
                      onChange={e => setNuevoIdioma(e.target.value)}
                    >
                      <option value="">Elige un idioma…</option>
                      {IDIOMAS.map(({ code, label }) => (
                        <option key={code} value={code} disabled={Boolean(mapaIdiomas[code])}>
                          {label} ({code.toUpperCase()}){mapaIdiomas[code] ? ' — ya existe' : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => void anadirIdioma()}
                      disabled={!nuevoIdioma || creandoIdioma}
                      className="btn-primary text-sm flex items-center gap-1.5"
                    >
                      {creandoIdioma ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                      Crear
                    </button>
                    <button
                      onClick={() => { setMostrarAltaIdioma(false); setNuevoIdioma(''); }}
                      className="btn-ghost text-sm"
                    >
                      Cancelar
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setMostrarAltaIdioma(true)}
                    className="btn-ghost text-sm flex items-center gap-1.5"
                  >
                    <Plus size={15} /> Añadir idioma
                  </button>
                )}

                {faltanPrincipales.length > 0 && (
                  <button
                    onClick={() => void crearPrincipales()}
                    disabled={progresoPrincipales !== null}
                    className="btn-ghost text-sm flex items-center gap-1.5"
                  >
                    {progresoPrincipales
                      ? <><Loader2 size={15} className="animate-spin" /> {progresoPrincipales}</>
                      : <><Globe2 size={15} /> Crear DE, ES, IT, FR</>}
                  </button>
                )}

                <div className="flex flex-wrap items-center gap-2 ml-auto">
                  <button
                    onClick={() => void guardarTodo()}
                    disabled={guardando || !idiomaActivo}
                    className="btn-primary text-sm flex items-center gap-1.5"
                  >
                    {guardando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    Guardar todo
                  </button>
                  {idiomaActivo && idiomaActivo !== idiomaBase && (
                    <button
                      onClick={() => void eliminarIdioma()}
                      disabled={eliminando}
                      className="btn-danger text-sm flex items-center gap-1.5"
                    >
                      {eliminando ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                      Eliminar idioma
                    </button>
                  )}
                </div>
              </div>

              {/* Los 14 campos */}
              {!idiomaActivo ? (
                <EmptyState
                  title="Este anuncio no tiene ningún idioma"
                  description="Añade un idioma para empezar a escribir sus textos."
                />
              ) : (
                <div className="space-y-4">
                  {COPY_FIELDS.map(campo => {
                    const valor = textos[campo] ?? '';
                    const estado = estadoCampos[campo] || {};
                    const ocupado = estado.ocupado;
                    const idInput = `copy-${campo}`;
                    return (
                      <div key={campo} className="card">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="min-w-0">
                            <label htmlFor={idInput} className="label mb-0">{ETIQUETAS_CAMPO[campo]}</label>
                            <p className="text-xs text-slate-500">{AYUDA_CAMPO[campo]}</p>
                          </div>
                          {campo === 'name' && (
                            <span className={classNames(
                              'text-xs shrink-0 tabular-nums',
                              valor.length >= MAX_TITULO ? 'text-rose-600' : 'text-slate-400',
                            )}>
                              {valor.length}/{MAX_TITULO}
                            </span>
                          )}
                        </div>

                        {campo === 'name' ? (
                          <input
                            id={idInput}
                            type="text"
                            className="input"
                            maxLength={MAX_TITULO}
                            value={valor}
                            onChange={e => editar(campo, e.target.value)}
                          />
                        ) : (
                          <TextareaCrece id={idInput} valor={valor} onChange={v => editar(campo, v)} />
                        )}

                        {/* `min-h-[2.75rem]` en móvil: con `py-1.5` estos botones
                            medían 28px de alto, la mitad del área de toque que
                            necesita un dedo, y están pegados unos a otros. En
                            pantalla grande vuelven a su tamaño compacto: son
                            catorce campos con tres botones cada uno. */}
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <button
                            onClick={() => void guardarCampo(campo)}
                            disabled={Boolean(ocupado)}
                            className="btn-secondary text-xs min-h-[2.75rem] sm:min-h-0 py-1.5 px-3 flex items-center gap-1.5"
                          >
                            {ocupado === 'guardar'
                              ? <Loader2 size={13} className="animate-spin" />
                              : <Save size={13} />}
                            Guardar
                          </button>
                          {campo === 'name' && (
                            <button
                              onClick={() => setTitulosAbierto(true)}
                              disabled={Boolean(ocupado)}
                              className="btn-ghost text-xs min-h-[2.75rem] sm:min-h-0 py-1.5 px-3 flex items-center gap-1.5"
                            >
                              <Lightbulb size={13} /> Idear título con IA
                            </button>
                          )}
                          <button
                            onClick={() => abrirOpcionesIA(campo)}
                            disabled={Boolean(ocupado)}
                            className="btn-ghost text-xs min-h-[2.75rem] sm:min-h-0 py-1.5 px-3 flex items-center gap-1.5"
                          >
                            {ocupado === 'ia'
                              ? <Loader2 size={13} className="animate-spin" />
                              : <Sparkles size={13} />}
                            Generar IA
                          </button>
                          <button
                            onClick={() => void traducirCampo(campo)}
                            disabled={Boolean(ocupado)}
                            className="btn-ghost text-xs min-h-[2.75rem] sm:min-h-0 py-1.5 px-3 flex items-center gap-1.5"
                          >
                            {ocupado === 'traducir'
                              ? <Loader2 size={13} className="animate-spin" />
                              : <Languages size={13} />}
                            Traducir y guardar
                          </button>
                          {estado.mensaje && (
                            <span className={classNames('text-xs w-full sm:w-auto', CLASES_TONO[estado.tono ?? 'info'])}>
                              {estado.mensaje}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Opciones de enriquecimiento antes de generar un campo suelto */}
      <Modal
        isOpen={campoIA !== null}
        onClose={() => setCampoIA(null)}
        title={campoIA ? `Generar "${ETIQUETAS_CAMPO[campoIA]}" con IA` : 'Generar con IA'}
      >
        <p className="text-sm text-slate-500 mb-4">
          Elige qué información adicional mandamos al modelo para enriquecer el texto.
        </p>

        <label className="flex items-start gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={opcionesIA.incluirAmenities}
            onChange={e => setOpcionesIA(prev => ({ ...prev, incluirAmenities: e.target.checked }))}
          />
          <span>
            <span className="block text-sm font-medium">Amenities</span>
            <span className="block text-xs text-slate-500">
              Enviar la lista de servicios y equipamiento del anuncio
            </span>
          </span>
        </label>

        <label className={classNames(
          'flex items-start gap-3 mb-4',
          numFotos === 0 ? 'opacity-50' : 'cursor-pointer',
        )}>
          <input
            type="checkbox"
            className="mt-1"
            disabled={numFotos === 0}
            checked={opcionesIA.incluirFotos && numFotos > 0}
            onChange={e => setOpcionesIA(prev => ({ ...prev, incluirFotos: e.target.checked }))}
          />
          <span>
            <span className="block text-sm font-medium">Fotos del anuncio</span>
            <span className="block text-xs text-slate-500">
              {numFotos === 0
                ? 'Este anuncio no tiene fotos en Hostify'
                : `El modelo verá las fotos para describir mejor el espacio (${numFotos} disponibles)`}
            </span>
          </span>
        </label>

        <div className="mb-5">
          <label htmlFor="ia-instrucciones" className="label">
            Instrucciones adicionales <span className="text-slate-400 font-normal">(opcional)</span>
          </label>
          <textarea
            id="ia-instrucciones"
            className="input"
            rows={3}
            placeholder="Ej: destaca las vistas al mar, menciona que está recién reformado..."
            value={opcionesIA.instrucciones}
            onChange={e => setOpcionesIA(prev => ({ ...prev, instrucciones: e.target.value }))}
          />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={() => setCampoIA(null)} className="btn-ghost">Cancelar</button>
          <button
            onClick={() => { if (campoIA) void generarCampo(campoIA, opcionesIA); }}
            className="btn-primary flex items-center gap-1.5"
          >
            <Sparkles size={16} /> Generar
          </button>
        </div>
      </Modal>

      {anuncioId !== null && (
        <>
          <GenerarTodoFlujo
            abierto={generarTodoAbierto}
            anuncioId={anuncioId}
            ficha={fichaAnuncio}
            idioma={IDIOMA_IA}
            textosActuales={textosReferencia}
            numFotos={numFotos}
            onCerrar={() => setGenerarTodoAbierto(false)}
            onAplicar={aplicarGeneracion}
          />
          <TraducirTodoFlujo
            abierto={traducirTodoAbierto}
            anuncioId={anuncioId}
            idiomaOrigen={IDIOMA_IA}
            campos={camposTraduccion}
            idiomasDestino={destinosTraduccion}
            onCerrar={() => setTraducirTodoAbierto(false)}
            onTerminado={() => { setTraducirTodoAbierto(false); void cargar(anuncioId); }}
          />
          <ModalTitulos
            abierto={titulosAbierto}
            anuncioId={anuncioId}
            idioma={idiomaActivo ?? idiomaBase}
            onCerrar={() => setTitulosAbierto(false)}
            onElegir={elegirTitulo}
          />
          <ModalHistorial
            abierto={historialAbierto}
            anuncioId={anuncioId}
            onCerrar={() => setHistorialAbierto(false)}
            onAplicar={aplicarHistorial}
          />
        </>
      )}
    </div>
  );
}
